import * as React from 'react';

import { enumBookingStatus, Weight } from '@rematter/frontend-lib-api-client';
import { Button, DialogContent, DialogOverlay } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { $updateLoad } from '~/modules/trade/_state/loadDetail.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectBooking } from '~/shared/components/select-fields/SelectBooking';

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  loadId: string;
  currentBookingId?: string | null;
  heavyWeight?: Weight | null;
  lightWeight?: Weight | null;
  onSuccess?: () => void;
};

export const LoadAssignBookingPopover: React.FC<Props> = ({
  isOpen,
  onOpenChange,
  loadId,
  currentBookingId,
  heavyWeight,
  lightWeight,
  onSuccess,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const [selectedBookingId, setSelectedBookingId] = React.useState<
    string | null
  >(currentBookingId ?? null);
  const [isLoading, setIsLoading] = React.useState(false);

  const updateLoad = useSetAtom($updateLoad);

  const handleBookingChange = React.useCallback((value: string | undefined) => {
    setSelectedBookingId(value ?? null);
  }, []);

  const handleAssign = React.useCallback(async () => {
    try {
      setIsLoading(true);
      await updateLoad({
        id: loadId,
        bookingId: selectedBookingId ?? undefined,
        heavyWeight,
        lightWeight,
      });

      const isAssigning = !currentBookingId && selectedBookingId;
      const isReassigning = currentBookingId && selectedBookingId;
      const isRemoving = currentBookingId && !selectedBookingId;

      if (isAssigning) {
        toast.success(t.loadDetail.messages.bookingAssigned);
      } else if (isReassigning) {
        toast.success(t.loadDetail.messages.bookingReassigned);
      } else if (isRemoving) {
        toast.success(t.loadDetail.messages.bookingRemoved);
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.loadDetail.messages.bookingAssignmentError,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    loadId,
    selectedBookingId,
    currentBookingId,
    updateLoad,
    onSuccess,
    onOpenChange,
    t,
    toast,
    heavyWeight,
    lightWeight,
  ]);

  const handleCancel = React.useCallback(() => {
    setSelectedBookingId(currentBookingId ?? null);
    onOpenChange(false);
  }, [currentBookingId, onOpenChange]);

  const isAssigning = !currentBookingId;
  const hasChanged = selectedBookingId !== currentBookingId;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@LoadAssignBookingPopover"
    >
      <DialogContent className="max-w-md">
        <div className="p-6">
          <h2 className="mb-4 text-lg font-semibold">
            {isAssigning
              ? t.loadDetail.actions.assignBooking
              : t.loadDetail.actions.reassignBooking}
          </h2>

          <div className="mb-6">
            <SelectBooking
              value={selectedBookingId}
              onChange={handleBookingChange}
              label={t.loadDetail.form.booking}
              statuses={[enumBookingStatus.Open]}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onPress={handleCancel}
              isDisabled={isLoading}
            >
              {t.loadDetail.actions.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleAssign}
              isDisabled={!hasChanged || isLoading}
              isLoading={isLoading}
            >
              {isAssigning
                ? t.loadDetail.actions.assign
                : t.loadDetail.actions.reassign}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
